import { ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { filter } from 'rxjs';

/** Session key: set before `location.reload()` so a subsequent failure navigates to 404. */
export const CHUNK_RELOAD_SESSION_KEY = 'joii:chunk-reload-attempted';

/**
 * Session key for reloads triggered from the ErrorHandler (chunk failures outside router navigation, e.g. `@defer`
 * or a manual `import()`). Unlike {@link CHUNK_RELOAD_SESSION_KEY} it is not cleared on `NavigationEnd`, so these
 * reload at most once per session and a chunk that keeps failing after load can't cause a reload loop.
 */
export const CHUNK_RELOAD_ERROR_HANDLER_SESSION_KEY = 'joii:chunk-reload-attempted-error-handler';

/**
 * Chunk recovery for the current page load: `reloading` once a reload has been triggered (later reports of the
 * failure are dropped), `gave-up` once the router has already reloaded and is showing the not-found page.
 */
let recoveryState: 'idle' | 'reloading' | 'gave-up' = 'idle';

function reloadForChunkFailure(): void {
  recoveryState = 'reloading';
  location.reload();
}

function messageFromError(error: unknown): string {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) {
    const base = `${error.name} ${error.message}`;
    const cause = (error as Error & { cause?: unknown }).cause;
    if (cause) return `${base} ${messageFromError(cause)}`;
    return base;
  }
  if (typeof error === 'object') {
    const rec = error as { rejection?: unknown; ngOriginalError?: unknown; message?: unknown };
    const nested = rec.rejection ?? rec.ngOriginalError;
    if (nested != null && nested !== error) return messageFromError(nested);
    if (typeof rec.message === 'string') return rec.message;
  }
  return String(error);
}

/**
 * True when a lazy route failed because the JS chunk could not be loaded (stale deploy, network, etc.).
 * Kept narrow so resolver/guard failures are not treated as chunk errors.
 */
export function isChunkLoadFailure(error: unknown): boolean {
  const text = messageFromError(error);
  if (!text) return false;

  if (/failed to fetch dynamically imported module/i.test(text)) return true;
  if (/error loading dynamically imported module/i.test(text)) return true;
  if (/importing a module script failed/i.test(text)) return true;
  if (/failed to load module script/i.test(text)) return true;
  if (/is not a valid JavaScript MIME type/i.test(text)) return true;
  if (/loading chunk [\d]+ failed/i.test(text)) return true;
  if (/import\(\) failed/i.test(text)) return true;
  if (/\bChunkLoadError\b/i.test(text)) return true;

  return false;
}

export interface ChunkLoadRecoveryOptions {
  /** Path segment for the static not-found route (no leading slash). */
  notFoundPath: string;
}

/**
 * On lazy-route chunk load failure: reload once (sessionStorage), then navigate to `notFoundPath`.
 * Clears the reload flag on successful `NavigationEnd`.
 */
export function provideChunkLoadRecovery(options: ChunkLoadRecoveryOptions) {
  return provideAppInitializer(() => {
    const router = inject(Router);
    const notFoundUrl = `/${options.notFoundPath.replace(/^\//, '')}`;

    router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
      } catch {
        /* ignore */
      }
    });

    router.events.pipe(filter((e): e is NavigationError => e instanceof NavigationError)).subscribe((event) => {
      if (!isChunkLoadFailure(event.error) || recoveryState === 'reloading') return;

      let pending: string | null = null;
      try {
        pending = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
      } catch {
        /* ignore */
      }

      if (pending === '1') {
        recoveryState = 'gave-up';
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
        } catch {
          /* ignore */
        }

        const raw = event.error;
        const err = raw instanceof Error ? raw : new Error(messageFromError(raw) || 'Chunk load failed after reload');
        Sentry.captureException(err, {
          tags: {
            chunk_load_recovery: 'unrecoverable_after_reload',
          },
          extra: {
            navigationId: event.id,
            attemptedUrl: event.url,
            routerUrl: router.url,
            notFoundUrl,
          },
        });

        void router.navigateByUrl(notFoundUrl, { replaceUrl: true });
        return;
      }

      try {
        sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
      } catch {
        /* ignore */
      }
      reloadForChunkFailure();
    });
  });
}

/**
 * Wraps an ErrorHandler (Sentry's) so a chunk/dynamic-import load failure (stale deploy) triggers one full page
 * reload instead of being reported. Covers failures outside router navigation, and drops the duplicate report of a
 * failed lazy route that {@link provideChunkLoadRecovery} is already reloading for. Everything else is delegated.
 */
export function createChunkLoadErrorHandler(delegate: ErrorHandler): ErrorHandler {
  return new ChunkLoadErrorHandler(delegate);
}

class ChunkLoadErrorHandler extends ErrorHandler {
  constructor(private readonly delegate: ErrorHandler) {
    super();
  }

  override handleError(error: unknown): void {
    if (isChunkLoadFailure(error) && recoveryState !== 'gave-up') {
      if (recoveryState === 'reloading') return;

      // Only reload once the flag is persisted; without it a chunk that keeps failing could reload forever.
      let canReload = false;
      try {
        if (sessionStorage.getItem(CHUNK_RELOAD_ERROR_HANDLER_SESSION_KEY) !== '1') {
          sessionStorage.setItem(CHUNK_RELOAD_ERROR_HANDLER_SESSION_KEY, '1');
          canReload = true;
        }
      } catch {
        /* ignore */
      }

      if (canReload) {
        reloadForChunkFailure();
        return;
      }
    }
    this.delegate.handleError(error);
  }
}

/** Test-only: resets the per-page recovery state. */
export function resetChunkLoadRecoveryStateForTesting(): void {
  recoveryState = 'idle';
}
